import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import HelpArticleContent from '../../components/HelpArticleContent';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  Search,
  Book,
  ChevronRight,
  Loader2,
  HelpCircle,
  PlayCircle,
  Smartphone,
  Clock,
  FileText,
  MessageSquare,
  ClipboardList,
} from 'lucide-react';

// Icon mapping
const ICON_MAP = {
  PlayCircle,
  Smartphone,
  Clock,
  FileText,
  MessageSquare,
  ClipboardList,
  HelpCircle,
  Book
};

const MobileHelp = () => {
  const navigate = useNavigate();
  const { api } = useMobileAuth();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  
  // Navigation state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Fetch help content
  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/mobile/help/content');
      
      if (response.data.from_database) {
        setCategories(response.data.categories);
        setArticles(response.data.articles);
      }
    } catch (error) {
      console.error('Failed to fetch help content:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Get current category data
  const currentCategory = selectedCategory 
    ? categories.find(c => c.key === selectedCategory) 
    : null;
  
  // Get articles for current category
  const currentArticles = selectedCategory
    ? articles.filter(a => a.category_key === selectedCategory)
    : [];

  // Filter by search
  const filteredCategories = searchQuery.trim()
    ? categories.filter(cat => {
        const categoryArticles = articles.filter(a => a.category_key === cat.key);
        return categoryArticles.some(article =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          article.content.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
    : categories;

  const filteredArticles = searchQuery.trim()
    ? articles.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currentArticles;

  // Render markdown-like content
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let listItems = [];
    let currentListType = null;

    const flushList = () => {
      if (listItems.length > 0) {
        if (currentListType === 'ordered') {
          elements.push(
            <ol key={`ol-${elements.length}`} className="list-decimal list-inside space-y-1 mb-3 ml-1 text-[var(--m-text-body)]">
              {listItems}
            </ol>
          );
        } else {
          elements.push(
            <ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-1 mb-3 ml-1 text-[var(--m-text-body)]">
              {listItems}
            </ul>
          );
        }
        listItems = [];
        currentListType = null;
      }
    };

    const parseInlineBold = (text) => {
      if (!text.includes('**')) return text;
      const parts = text.split(/\*\*(.+?)\*\*/g);
      return parts.map((part, j) => 
        j % 2 === 1 ? <strong key={j} className="text-[var(--m-text)]">{part}</strong> : part
      );
    };

    lines.forEach((line, i) => {
      const numberedMatch = line.match(/^(\d+)\.\s(.+)/);
      if (numberedMatch) {
        if (currentListType !== 'ordered') {
          flushList();
          currentListType = 'ordered';
        }
        listItems.push(<li key={`li-${i}`}>{parseInlineBold(numberedMatch[2])}</li>);
        return;
      }

      if (line.startsWith('- ') || line.startsWith('• ')) {
        if (currentListType !== 'unordered') {
          flushList();
          currentListType = 'unordered';
        }
        listItems.push(<li key={`li-${i}`}>{parseInlineBold(line.substring(2))}</li>);
        return;
      }

      flushList();

      if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <h4 key={`h-${i}`} className="font-semibold mt-4 mb-2 text-[var(--m-text)]">{line.replace(/\*\*/g, '')}</h4>
        );
        return;
      }

      if (line.includes('**')) {
        elements.push(
          <p key={`p-${i}`} className="mb-2 text-[var(--m-text-body)]">{parseInlineBold(line)}</p>
        );
        return;
      }

      if (!line.trim()) {
        elements.push(<div key={`sp-${i}`} className="h-2" />);
        return;
      }

      elements.push(<p key={`t-${i}`} className="mb-2 text-[var(--m-text-body)]">{line}</p>);
    });

    flushList();
    return elements;
  };

  // Navigation handlers
  const handleCategoryClick = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
  };

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    } else {
      navigate('/mobile/dashboard');
    }
  };

  // Get icon for category
  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Book;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <MobileLayout>
    <div className="min-h-full bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] border-b border-[var(--m-border)] sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleBack}
              className="text-[var(--m-text-secondary)] hover:text-[var(--m-text)] -ml-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-[var(--m-text)] font-semibold">
                {selectedArticle 
                  ? selectedArticle.title 
                  : selectedCategory 
                    ? currentCategory?.title 
                    : 'Help Center'}
              </h1>
              {!selectedArticle && !selectedCategory && (
                <p className="text-[var(--m-text-secondary)] text-xs">Guides & tutorials</p>
              )}
            </div>
          </div>
          <Book className="h-5 w-5 text-[#ffd108]" />
        </div>
        
        {/* Search - only show on categories view */}
        {!selectedCategory && !selectedArticle && (
          <div className="px-4 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--m-text-muted)]" />
              <Input
                placeholder="Search help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)] placeholder:text-[var(--m-text-muted)]"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Categories View */}
        {!selectedCategory && !selectedArticle && (
          <div className="space-y-3">
            {searchQuery.trim() ? (
              // Search results
              <>
                <p className="text-[var(--m-text-secondary)] text-sm mb-4">
                  {filteredArticles.length} result{filteredArticles.length !== 1 ? 's' : ''} found
                </p>
                {filteredArticles.map((article) => {
                  const category = categories.find(c => c.key === article.category_key);
                  return (
                    <Card 
                      key={article.id}
                      className="bg-[var(--m-bg-card)] border-[var(--m-border)] active:bg-[var(--m-bg-elevated)]"
                      onClick={() => {
                        setSelectedCategory(article.category_key);
                        setSelectedArticle(article);
                        setSearchQuery('');
                      }}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#ffd108]/10 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-5 w-5 text-[#ffd108]" />
                          </div>
                          <div>
                            <p className="text-[var(--m-text)] font-medium">{article.title}</p>
                            <p className="text-[var(--m-text-muted)] text-xs">{category?.title}</p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-600" />
                      </CardContent>
                    </Card>
                  );
                })}
                {filteredArticles.length === 0 && (
                  <div className="text-center py-12">
                    <HelpCircle className="h-12 w-12 mx-auto text-gray-600 mb-3" />
                    <p className="text-[var(--m-text-secondary)]">No articles found</p>
                  </div>
                )}
              </>
            ) : (
              // Categories list
              filteredCategories.map((category) => {
                const Icon = getIcon(category.icon);
                const articleCount = articles.filter(a => a.category_key === category.key).length;
                return (
                  <Card 
                    key={category.key}
                    className="bg-[var(--m-bg-card)] border-[var(--m-border)] active:bg-[var(--m-bg-elevated)]"
                    onClick={() => handleCategoryClick(category.key)}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#ffd108]/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-[#ffd108]" />
                        </div>
                        <div>
                          <p className="text-[var(--m-text)] font-medium">{category.title}</p>
                          <p className="text-[var(--m-text-muted)] text-xs">
                            {articleCount} article{articleCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Articles List View */}
        {selectedCategory && !selectedArticle && (
          <div className="space-y-3">
            <p className="text-[var(--m-text-secondary)] text-sm mb-2">{currentCategory?.description}</p>
            {currentArticles.map((article) => (
              <Card 
                key={article.id}
                className="bg-[var(--m-bg-card)] border-[var(--m-border)] active:bg-[var(--m-bg-elevated)]"
                onClick={() => handleArticleClick(article)}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#ffd108] flex-shrink-0" />
                    <div>
                      <p className="text-[var(--m-text)] font-medium">{article.title}</p>
                      {article.badge && (
                        <Badge className="bg-[#ffd108]/20 text-[#ffd108] text-xs mt-1">
                          {article.badge}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-600" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Article Content View */}
        {selectedArticle && (
          <div className="pb-8">
            {selectedArticle.badge && (
              <Badge className="bg-[#ffd108]/20 text-[#ffd108] mb-4">
                {selectedArticle.badge}
              </Badge>
            )}
            <div className="text-sm leading-relaxed">
              <HelpArticleContent
                content={selectedArticle.content}
                format={selectedArticle.content_format || 'markdown'}
                className="prose-invert"
              />
            </div>
            
            {/* Related articles */}
            {currentArticles.length > 1 && (
              <div className="mt-8 pt-6 border-t border-[var(--m-border)]">
                <h3 className="text-[var(--m-text)] font-semibold mb-3">Related Articles</h3>
                <div className="space-y-2">
                  {currentArticles
                    .filter(a => a.id !== selectedArticle.id)
                    .slice(0, 3)
                    .map((article) => (
                      <button
                        key={article.id}
                        onClick={() => handleArticleClick(article)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-[var(--m-bg-card)] text-left active:bg-[var(--m-bg-elevated)]"
                      >
                        <div className="w-2 h-2 rounded-full bg-[#ffd108]" />
                        <span className="text-[var(--m-text-body)] text-sm">{article.title}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </MobileLayout>
  );
};

export default MobileHelp;
